<template>
    <v-form id="m-form" class="calculator-comp" ref="form" v-model="valid" @submit.prevent="handleValidation">
        <v-container fluid class="d-flex flex-column" style="gap: 8px">
            <v-row>
                <v-col cols="12">
                    <div class="calculator-comp__addresses mx-n4">
                        <AddressesBox
                            :sharedAddresses="form.addresses"
                            :isAddressesRemovable="isAddressesRemovable"
                            :maxAddresses="2"
                            @address-change="handleAddressChange"
                            @gis-api-call="apiCallCount += 1"
                        />
                    </div>
                </v-col>

                <v-col cols="12">
                    <v-row>
                        <v-col cols="12" sm="4">
                            <v-switch
                                class="v-switch--inverted"
                                v-model="form.direction"
                                label="Туда-обратно"
                                hide-details
                                :disabled="!form.addresses.every((x) => x.selected)"
                                color="#0eccaa"
                                @change="onDirectionChange"
                            ></v-switch>
                        </v-col>
                        <v-col v-if="form.direction" cols="12" sm="4">
                            <v-menu
                                v-model="isWaitTimepickerOpen"
                                scroll-strategy="none"
                                transition="fade-transition"
                                max-width="200px"
                                min-width="1px"
                                :close-on-content-click="false"
                                content-class="calculator-comp__timepicker"
                            >
                                <template v-slot:activator="{ props }">
                                    <v-text-field
                                        v-model="form.wait"
                                        :rules="[
                                            (val) => val.split(':').every((x) => /^\d+$/.test(x)) || 'Неверный формат',
                                        ]"
                                        label="Ожидание"
                                        placeholder="чч:мм"
                                        :hint="`${isPetersburgRegion ? '15 минут' : '1 час'} бесплатно`"
                                        persistent-hint
                                        readonly
                                        variant="outlined"
                                        max-width="240px"
                                        bg-color="#fffdfd"
                                        v-bind="props"
                                    >
                                        <template v-slot:prepend-inner>
                                            <v-icon :icon="mdiTimerOutline" color="#424242"></v-icon>
                                        </template>
                                    </v-text-field>
                                </template>
                                <v-card class="rounded-lg">
                                    <MTimepicker
                                        :value="form.wait"
                                        minuteInterval="15"
                                        max-hours="6"
                                        @change="handleWaitTimeChange"
                                    />
                                </v-card>
                            </v-menu>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="12">
                    <v-row>
                        <v-col cols="12" sm="4"
                            ><v-switch
                                class="v-switch--inverted"
                                v-model="form.support"
                                label="Медицинское сопровождение"
                                hide-details
                                color="#0eccaa"
                            ></v-switch
                        ></v-col>
                        <v-col cols="12" sm="4">
                            <v-switch
                                class="v-switch--inverted"
                                v-model="form.reanimation"
                                label="Реанимационная бригада"
                                hide-details
                                color="#0eccaa"
                            ></v-switch>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch
                                class="v-switch--inverted"
                                v-model="form.oxygen"
                                label="Нужен кислород"
                                hide-details
                                color="#0eccaa"
                            ></v-switch>
                        </v-col>
                    </v-row>
                </v-col>

                <v-col cols="12">
                    <v-text-field
                        v-model="form.weight"
                        :rules="[
                            () => !!form.weight || 'Обязательное поле',
                            () => !isNaN(form.weight) || 'Неверный формат',
                        ]"
                        label="Вес пациента*"
                        placeholder="Вес пациента* (кг)"
                        variant="outlined"
                        max-width="200"
                        bg-color="#fffdfd"
                        type="text"
                        v-mask="'###'"
                        hide-spin-buttons
                        clearable
                    >
                        <template v-slot:prepend-inner>
                            <v-icon :icon="mdiScale" color="#424242"></v-icon>
                        </template>
                    </v-text-field>
                </v-col>

                <v-col cols="12">
                    <v-autocomplete
                        v-model="form.comment"
                        item-title="title"
                        item-value="title"
                        label="Информация"
                        placeholder="Что нам ещё важно знать..."
                        persistent-placeholder
                        variant="outlined"
                        bg-color="#fffdfd"
                        chips
                        multiple
                        readonly
                        class="calculator-comp__select"
                        @click.stop="isSheetOpen = !isSheetOpen"
                    >
                        <template v-slot:chip="{ props, item }">
                            <v-chip v-bind="props" :text="item.raw.title" variant="flat" color="#0ECCAA"></v-chip>
                        </template>

                        <template v-slot:prepend-inner>
                            <v-icon :icon="mdiListBox" color="#424242"></v-icon>
                        </template>
                    </v-autocomplete>

                    <v-bottom-sheet v-model="isSheetOpen" scroll-strategy="block">
                        <v-card title="Выберите дополнительную информацию:">
                            <template v-slot:text>
                                <v-chip-group
                                    v-model="form.comment"
                                    multiple
                                    column
                                    selected-class="calculator-comp__comment-chip"
                                >
                                    <v-chip
                                        v-for="(tag, index) in criterias"
                                        :key="index"
                                        :value="tag.title"
                                        :disabled="
                                            (tag.title == 'только лёжа' && form.comment.includes('только сидя')) ||
                                            (tag.title == 'присаживается' && form.comment.includes('только сидя')) ||
                                            (tag.title == 'только сидя' && form.comment.includes('присаживается')) ||
                                            (tag.title == 'только сидя' && form.comment.includes('только лёжа')) ||
                                            (tag.title == 'на обследование' && form.comment.includes('выписка')) ||
                                            (tag.title == 'гемодиализ' && form.comment.includes('выписка')) ||
                                            (tag.title == 'выписка' && form.comment.includes('на обследование')) ||
                                            (tag.title == 'на обследование' && form.comment.includes('гемодиализ')) ||
                                            (tag.title == 'выписка' && form.comment.includes('гемодиализ')) ||
                                            (tag.title == 'гемодиализ' && form.comment.includes('на обследование')) ||
                                            (tag.title == 'пациент поедет один' &&
                                                form.comment.includes('с пациентом 2 сопровождающих')) ||
                                            (tag.title == 'с пациентом 2 сопровождающих' &&
                                                form.comment.includes('пациент поедет один')) ||
                                            (tag.title == 'только лёжа' &&
                                                form.comment.includes('пациент на электрокаляске')) ||
                                            (tag.title == 'пациент на электрокаляске' &&
                                                form.comment.includes('только лёжа'))
                                        "
                                        >{{ tag.title }}</v-chip
                                    >
                                </v-chip-group>
                            </template>
                            <template v-slot:actions>
                                <div v-if="form.comment.length" class="pl-4">
                                    <v-btn
                                        text="Очистить"
                                        size="x-large"
                                        variant="tonal"
                                        rounded="lg"
                                        elevation="0"
                                        class="calculator-comp__button"
                                        @click="form.comment = []"
                                    ></v-btn>
                                </div>

                                <v-spacer></v-spacer>

                                <div class="pr-4">
                                    <v-btn
                                        class="calculator-comp__button calculator-comp__button--primary ms-auto"
                                        size="x-large"
                                        variant="flat"
                                        rounded="lg"
                                        elevation="0"
                                        color="#4bace3"
                                        @click="isSheetOpen = !isSheetOpen"
                                        >Закрыть</v-btn
                                    >
                                </div>
                            </template>
                        </v-card>
                    </v-bottom-sheet>
                </v-col>
            </v-row>

            <v-row>
                <v-col v-if="isError" cols="12">
                    <v-alert v-if="isError" border="start" color="#ee6870" variant="tonal">
                        {{ notice }}
                    </v-alert>
                </v-col>
                <v-col cols="12">
                    <div v-if="predictedPrice == 0 || predictedPrice === '' || isError" class="d-flex">
                        <v-btn
                            class="calculator-comp__button calculator-comp__button--primary mx-auto"
                            color="#4bace3"
                            size="x-large"
                            rounded="lg"
                            elevation="0"
                            type="submit"
                            >Рассчитать</v-btn
                        >
                    </div>

                    <template v-else-if="predictedPrice && !isError">
                        <div class="calculator-comp__price">
                            <v-row align="baseline">
                                <v-col cols="auto" class="calculator-comp__price-title"
                                    >Ориентировочная стоимость поездки составит</v-col
                                >
                                <v-col
                                    cols="auto"
                                    class="calculator-comp__price-amount"
                                    style="color: var(--color-green)"
                                    >{{ predictedPrice }} руб.</v-col
                                >
                            </v-row>
                            <v-row>
                                <v-col cols="auto"
                                    ><v-btn
                                        class="calculator-comp__button mx-auto"
                                        :class="{ 'calculator-comp__button--primary': isChanged }"
                                        color="#4bace3"
                                        size="x-large"
                                        rounded="lg"
                                        elevation="0"
                                        :disabled="!isChanged"
                                        type="submit"
                                        >Пересчитать</v-btn
                                    ></v-col
                                >
                                <v-col cols="auto"
                                    ><v-btn
                                        class="calculator-comp__button calculator-comp__button--primary mx-auto"
                                        color="#4bace3"
                                        size="x-large"
                                        rounded="lg"
                                        elevation="0"
                                        @click="pushToPage"
                                        >Заказать</v-btn
                                    ></v-col
                                >
                            </v-row>
                        </div>
                    </template>
                </v-col>
            </v-row>
        </v-container>
    </v-form>
</template>

<script setup>
import { mdiScale, mdiListBox, mdiTimerOutline } from '@mdi/js';
</script>

<script>
import AddressesBox from './AddressesBox/AddressesBox.vue';
import MTimepicker from './UI/Timepicker/MTimepicker.vue';

import CostsTable from '../data/costs.json';

import axios from 'axios';
import { mask } from 'vue-the-mask';
import tools from '@/assets/js/tools.js';

const DEFAULT_ADDRESSES = [
    {
        id: 0,
        selected: null,
        floor: null,
        isElevator: false,
        isElevatorCargo: false,
    },
    {
        id: 1,
        selected: null,
        floor: null,
        isElevator: false,
        isElevatorCargo: false,
    },
];

const INITIAL_FORM_DATA = {
    addresses: JSON.parse(JSON.stringify(DEFAULT_ADDRESSES)),
    direction: false,
    wait: '00:00',
    weight: '',
    support: false,
    reanimation: false,
    oxygen: false,
    comment: [],
};

const CRITERIAS = [
    { title: 'только лёжа' },
    { title: 'присаживается' },
    { title: 'только сидя' },
    { title: 'после инсульта' },
    { title: 'перелом шейки бедра' },
    { title: 'выписка' },
    { title: 'на обследование' },
    { title: 'гемодиализ' },
    { title: 'пациент поедет один' },
    { title: 'пациент передвигается с трудом' },
    { title: 'с пациентом 2 сопровождающих' },
    { title: 'пациент на электрокаляске' },
];

export default {
    components: {
        AddressesBox,
        MTimepicker,
    },

    directives: { mask },

    data() {
        return {
            notice: null,
            isShowOnlyContent: tools.isShowOnlyContent(),

            valid: false,
            form: JSON.parse(JSON.stringify(INITIAL_FORM_DATA)),
            criterias: [...CRITERIAS],
            isWaitTimepickerOpen: false,
            isSheetOpen: false,
            apiCallCount: 0,

            priceTable: CostsTable,
            distance: 0,
            duration: 0,
            predictedPrice: '',
            isChanged: false,
            fistCalculation: true,
        };
    },

    computed: {
        isAddressesRemovable() {
            return this.form.addresses.length > 2;
        },

        isPetersburgRegion() {
            return this.form.addresses.map((x) => x.selected?.region).includes('Санкт-Петербург');
        },

        isError() {
            return this.notice && this.notice.length;
        },
    },

    watch: {
        form: {
            handler() {
                this.isChanged = true;
            },
            deep: true,
        },

        ['form.weight'](newValue) {
            if (newValue > 600) {
                this.form.weight = 600;
            }
        },

        ['form.reanimation'](newValue) {
            if (newValue) {
                this.form.support = true;
            }
        },

        ['form.oxygen'](newValue) {
            if (newValue) {
                this.form.support = true;
            }
        },

        ['form.support'](newValue) {
            if (!newValue) {
                this.form.reanimation = false;
                this.form.oxygen = false;
            }
        },
    },

    created() {
        const loadedForm = this.loadForm('orderForm');

        if (loadedForm) {
            this.form = loadedForm;
        }
    },

    methods: {
        onDirectionChange() {
            if (!this.form.direction) {
                this.form.wait = '00:00';
                return;
            }

            this.form.wait = this.isPetersburgRegion ? '00:15' : '01:00';
        },

        handleWaitTimeChange(data) {
            this.form.wait = data;
        },

        handleAddressChange(addresses) {
            this.form.addresses = addresses;
        },

        loadForm(name) {
            try {
                var item = JSON.parse(localStorage.getItem(name));
                if (new Date().getTime() > item.expiry) {
                    localStorage.removeItem(name);
                    return null;
                }
                return item.data;
            } catch {
                return null;
            }
        },

        regAddrToString(intermediate, addr, label) {
            const joinedArr = [intermediate, addr, label]
                .filter(Boolean)
                .map((x) => x.split(', '))
                .join()
                .split(',');
            return [...new Set(joinedArr)].join(', ');
        },

        async handleValidation() {
            if (!this.valid) {
                return;
            }

            if (this.valid) {
                this.calcCost();
            }
        },

        async sendMessage(data) {
            const url = `https://api.telegram.org/bot${process.env.VUE_APP_TEST_BOT_TOKEN}/sendMessage`;

            // PREPARE TEXT FOR MESSAGE
            const text = `${
                this.notice ? '<code>стоимость не посчитана - было предложено обратиться к оператору</code>' : ''
            }
${process.env.VUE_APP_TEST_MARKER === 'true' ? '<b>ТЕСТ - </b>': ''}${
                this.fistCalculation ? 'РАСЧЕТ' : 'ПЕРЕРАСЧЁТ'
            } СТОИМОСТИ
<code>${window.parent.location.pathname}</code>
------------------------------------------------------
<u><i>Откуда:</i></u>
<b>${this.regAddrToString(
                this.form.addresses[0].selected.intermediate,
                this.form.addresses[0].selected.addressName,
                this.form.addresses[0].selected.label
            )}</b> (<code>${this.form.addresses[0].selected.point.lat},${
                this.form.addresses[0].selected.point.lon
            }</code>)
<i>Этаж:</i> <b>${this.form.addresses[0].floor ? this.form.addresses[0].floor : 'Не указан'}</b> | <i>Лифт:</i> <b>${
                this.form.addresses[0].isElevator ? 'Да' : 'Нет'
            }</b> | <i>Грузовой:</i> <b>${this.form.addresses[0].isElevatorCargo ? 'Да' : 'Нет'}</b>
------------------------------------------------------
<u><i>Куда:</i></u>
<b>${this.regAddrToString(
                this.form.addresses[this.form.addresses.length - 1].selected.intermediate,
                this.form.addresses[this.form.addresses.length - 1].selected.addressName,
                this.form.addresses[this.form.addresses.length - 1].selected.label
            )}</b> (<code>${this.form.addresses[this.form.addresses.length - 1].selected.point.lat},${
                this.form.addresses[this.form.addresses.length - 1].selected.point.lon
            }</code>)
<i>Этаж:</i> <b>${
                this.form.addresses[this.form.addresses.length - 1].floor
                    ? this.form.addresses[this.form.addresses.length - 1].floor
                    : 'Не указан'
            }</b> | <i>Лифт:</i> <b>${
                this.form.addresses[this.form.addresses.length - 1].isElevator ? 'Да' : 'Нет'
            }</b> | <i>Грузовой:</i> <b>${
                this.form.addresses[this.form.addresses.length - 1].isElevatorCargo ? 'Да' : 'Нет'
            }</b>
------------------------------------------------------
<a href="https://yandex.ru/maps/?rtext=${this.form.addresses
                .map((addr) => `${addr.selected.point.lat},${addr.selected.point.lon}`)
                .join('~')}&rtt=auto">Посмотреть маршрут - https://yandex.ru/maps/?rtext=${this.form.addresses
                .map((addr) => `${addr.selected.point.lat},${addr.selected.point.lon}`)
                .join('~')}&rtt=auto</a>
------------------------------------------------------
<u><i>Поездка:</i></u> <b>${this.form.direction ? 'туда-обратно' : 'в одну сторону'}</b> ${
                this.form.direction && this.form.wait
                    ? `| <u><i>Ожидание:</i></u> <b>${this.form.wait
                          .split(':')
                          .map((x, index) => (index === 0 ? x + ' ч.' : x + ' мин.'))
                          .join(' ')}</b>`
                    : ''
            }
<u><i>Расстояние:</i></u> <b>${this.form.direction ? this.distance * 2 : this.distance}</b> км.
<u><i>Продолжительность:</i></u> <b>${this.form.direction ? this.duration * 2 : this.duration}</b>
------------------------------------------------------
<u><i>Перевозка:</i></u> <b>${this.form.support ? 'медицинская' : 'санитарная'}</b> ${
                this.form.reanimation ? '| <b>реанимация</b>' : ''
            } ${this.form.oxygen ? '| <b>кислород</b>' : ''}
------------------------------------------------------
<u><i>Вес:</i></u> <b>${this.form.weight}</b> кг.
${
    this.form.comment.length > 0
        ? `------------------------------------------------------
<u><i>Дополнительная информация:</i></u>
${this.form.comment.map((x) => `• <b>${x}</b>`).join('\n')}
------------------------------------------------------`
        : '------------------------------------------------------'
}
 
<u><i>Стоимость:</i></u> <b>${this.predictedPrice}</b> руб.

------------------------------------------------------
<blockquote expandable>
<code>количество запросов к 2gis api: <b>${this.apiCallCount}</b></code>
</blockquote>
`;
            // --- END OF PREPARED TEXT ---

            const user_ids = [279250175, 322494230, 7048427415];

            user_ids.forEach(async (user_id) => {
                try {
                    const response = await axios.post(url, {
                        chat_id: user_id,
                        text: data ?? text,
                        parse_mode: 'HTML',
                    });
                    this.apiCallCount = 0;
                    console.log('Message sent:', response.data);
                } catch (error) {
                    console.error('Error sending message:', error);
                }
            });
        },

        pushToPage() {
            this.sendMessage(`🟢 Зафиксирован переход из калькулятора к форме оформления заказа`);
            tools.saveForm('orderForm', this.form);
            localStorage.setItem('isScrollToOrderForm', 'true');
            window.parent.location.href = '/';
        },

        transformRegions(fromRegion, toRegion) {
            const keyOptions = Object.keys(this.priceTable);

            // Функция для преобразования регионов в ключи
            function convertToKey(region) {
                if (region === 'Московская область') {
                    return 'МО';
                }
                return region;
            }
            fromRegion = convertToKey(fromRegion);
            toRegion = convertToKey(toRegion);

            // Список вариантов в порядке приоритета
            const possibleResults = [
                `${fromRegion} - ${toRegion}`,
                `${toRegion} - ${fromRegion}`,
                `${fromRegion} - другой город`,
                `${toRegion} - другой город`,
            ];
            for (var curResult of possibleResults) {
                if (keyOptions.includes(curResult)) {
                    return curResult;
                }
            }

            // В случае если нет предопределенных городов
            if (fromRegion === toRegion) {
                return 'Любой город - этот же город';
            }
            return 'Любой город - любой другой город';
        },

        async calcCost() {
            if (!this.isChanged) {
                return;
            }

            const { distance, duration } = await this.calculateDistMatrix();
            this.distance = Math.ceil(distance / 1000);
            this.duration = duration / 60;

            if (!this.distance || this.distance > 2000) {
                this.form.predictedPrice = 'невозможно определить';
                this.notice =
                    'Для таких ситуаций сложно рассчитать цену. Возможно слишком большая дистаниция. Уточните у оператора!';
                this.sendMessage();
                return;
            } else if (this.form.weight > 190) {
                this.form.predictedPrice = 'невозможно определить';
                this.notice =
                    'Для таких ситуаций сложно рассчитать цену. Возможно слишком большой вес. Уточните у оператора!';
                this.sendMessage();
                return;
            } else {
                this.notice = null;
            }

            if (this.form.addresses[0].selected) {
                const CONDITION = this.form.support ? (this.form.reanimation ? 'serious' : 'average') : 'ok';
                console.log(
                    this.transformRegions(
                        this.form.addresses[0].selected.region,
                        this.form.addresses[this.form.addresses.length - 1].selected.region
                    )
                );
                let regionPrices =
                    this.priceTable[
                        this.transformRegions(
                            this.form.addresses[0].selected.region,
                            this.form.addresses[this.form.addresses.length - 1].selected.region
                        )
                    ].conditions[CONDITION];

                // Преобразовываем диапазоны в числовые значения
                let numericDistance = Math.floor(parseInt(this.distance));

                // Поиск подходящего диапазона
                let selectedRange = null;

                for (const range in regionPrices.distance) {
                    const [min, max] = range.split('-').map(parseFloat);
                    if (numericDistance >= min && numericDistance <= max) {
                        selectedRange = range;
                        break;
                    }
                }

                if (regionPrices.distance[selectedRange].Excess === true) {
                    console.log('Excess condition avtivated');
                    numericDistance = Math.floor(parseFloat(this.distance)) - 40; // TODO: what is it
                }

                const basePrice = regionPrices.distance[selectedRange].baseCost;
                const pricePerKm = regionPrices.distance[selectedRange].additionalCost;
                const approximateActualRegionalPrice = basePrice + numericDistance * pricePerKm;

                const TRIP_COST = this.calculateTripByWeight(approximateActualRegionalPrice, this.form.weight);

                this.predictedPrice =
                    Math.ceil(this.form.direction ? TRIP_COST + (TRIP_COST - TRIP_COST * 0.2) : TRIP_COST) +
                    this.calculateWaitTimeCost() +
                    this.calculateFloorCost(this.form.weight) +
                    (this.calculateOxygen(this.form.weight, this.duration) ?? 0);
                this.notice = null;
            } else {
                this.notice = 'Недостаточно данных для рассчетов';
            }

            this.sendMessage();
            this.fistCalculation = false;
            this.isChanged = false;
        },

        calculateFloorCost(weight) {
            let floorCost = 0;
            let isFloors = this.form.addresses.filter((x) => !x.isElevator && x.floor);
            let floorCount = 0;

            if (isFloors && isFloors.length > 0) {
                floorCount = isFloors.map((x) => x.floor).reduce((acc, curr) => (acc += parseInt(curr)), 0);
                console.log('floorCount', floorCount);
            } else {
                return floorCost;
            }

            if (weight <= 90) {
                floorCost = floorCount * 300;
            } else if (weight > 90 && weight <= 120) {
                floorCost = floorCount * 350;
            } else if (weight > 120 && weight <= 160) {
                floorCost = floorCount * 400;
            } else if (weight > 160 && weight <= 190) {
                floorCost = floorCount * 450;
            }

            return this.form.direction ? floorCost * 2 : floorCost;
        },

        calculateWaitTimeCost() {
            if (!this.form.wait || !this.form.direction) {
                return 0;
            }

            const minutes =
                this.form.wait
                    .split(':')
                    .map(Number)
                    .reduce((acc, curr, index) => {
                        return (acc += index === 0 ? curr * 60 : curr);
                    }, 0) - (this.isPetersburgRegion ? 15 : 60);

            if (minutes <= 0) {
                return 0;
            } else {
                return (minutes / 60) * (this.isPetersburgRegion ? 3000 : 2000);
            }
        },

        calculateTripByWeight(cost, weight) {
            if (weight <= 90) {
                return cost; // return simple cost
            } else if (weight > 90 && weight <= 120) {
                return cost + 1500; // return cost with add
            } else if (weight > 120 && weight <= 160) {
                const sum = cost + 1500;
                return sum + sum * 0.5; // return cost with add half of it
            } else if (weight > 160 && weight <= 190) {
                const sum = cost + 1500;
                return sum * 2; // return two costs
            }
        },

        calculateOxygen(weight, duration) {
            if (!this.form.oxygen) {
                return;
            }
            const consumption = weight / 10 + 1;
            return Math.ceil(consumption * 25 * duration);
        },

        calculateWeightCost(weight) {
            const weightThreshold = 90; // Порог веса для изменения стоимости
            const baseWeightCost = this.priceTable.weight['До 90 кг за этаж'];
            const additionalWeightCost = this.priceTable.weight['После 90 кг за этаж'];
            const floorCount_FROM = parseInt(this.form.addresses[0].floor) ?? 0;
            const floorCount_TO = parseInt(this.form.addresses[this.form.addresses.length - 1].floor) ?? 0;

            var floorCount = 0;
            if (floorCount_FROM >= 2 || floorCount_FROM < 0) {
                if (!this.form.addresses[0].isElevator) {
                    if (floorCount_FROM < 0) {
                        floorCount += Math.max(0, 1 - floorCount_FROM);
                    } else if (floorCount_FROM >= 0) {
                        floorCount += floorCount_FROM === 0 ? 1 : floorCount_FROM;
                    }
                }
            }

            if (floorCount_TO >= 2 || floorCount_TO < 0) {
                if (!this.form.addresses[this.form.addresses.length - 1].isElevator) {
                    if (floorCount_TO < 0) {
                        floorCount += Math.max(0, 1 - floorCount_TO);
                    } else if (floorCount_TO >= 0) {
                        floorCount += floorCount_TO === 0 ? 1 : floorCount_TO;
                    }
                }
            }

            // Если вес пациента не превышает порог и чекбокс "Подъем на этаж" включен, возвращаем стоимость веса за этаж
            if (floorCount != 0) {
                if (weight <= weightThreshold) {
                    return baseWeightCost * floorCount;
                } else {
                    return additionalWeightCost * floorCount;
                }
            }

            // Если чекбокс выключен, возвращаем 0 (не учитываем стоимость подъема)
            return 0;
        },

        async calculateDistMatrix() {
            this.apiCallCount += 1;
            const TOKEN = '27b21cb1-37db-439a-8c9a-c27cd5f06f29';
            const apiUrl = 'https://routing.api.2gis.com/get_dist_matrix?key=' + TOKEN + '&version=2.0';

            // Проверяем, что у нас есть данные для начальной и конечной точек
            if (this.form.addresses[0].selected && this.form.addresses[this.form.addresses.length - 1].selected) {
                const requestData = {
                    points: [
                        {
                            lat: this.form.addresses[0].selected.point.lat,
                            lon: this.form.addresses[0].selected.point.lon,
                        },
                        {
                            lat: this.form.addresses[this.form.addresses.length - 1].selected.point.lat,
                            lon: this.form.addresses[this.form.addresses.length - 1].selected.point.lon,
                        },
                    ],
                    sources: [0],
                    targets: [1],
                    mode: 'driving',
                    start_time: new Date().toISOString(),
                    avoid: 'jam',
                };

                try {
                    const response = await fetch(apiUrl, {
                        method: 'POST',
                        body: JSON.stringify(requestData),
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    });

                    const data = await response.json();

                    if (data.routes && data.routes.length > 0) {
                        return data.routes[0];
                    } else {
                        console.error('Не удалось получить расстояние.');
                    }
                } catch (error) {
                    console.error('Произошла ошибка при запросе расстояния:', error);
                }
            } else {
                console.error('Отсутствуют данные для начальной и/или конечной точек.');
            }
        },
    },
};
</script>

<style lang="scss">
.calculator-comp {
    &__addresses {
        padding: 18px 16px 16px;
        border-radius: 10px;
        background: linear-gradient(90deg, rgba(74, 172, 226, 0.1) 7.76%, rgba(238, 104, 112, 0.1) 99.18%);
    }

    &__price {
        padding: 42px 67px;
        flex-direction: column;
        gap: 30px;
        align-items: flex-start;
        border-radius: 10px;
        background: linear-gradient(99deg, rgba(14, 204, 170, 0.2) 11.77%, rgba(74, 172, 226, 0.2) 83.93%);

        &-title {
            font-size: 20px;
            font-weight: 600;
            color: var(--color-grey);
            margin-bottom: 25px;
        }

        &-amount {
            font-size: 24px;
            font-style: normal;
            font-weight: 700;
        }
    }

    &__button {
        text-transform: none !important;
        letter-spacing: 0 !important;
        font-size: 18px !important;

        &--primary {
            &:hover {
                background-image: linear-gradient(180deg, #0eccaa, #4bace3);
            }
        }
    }

    // custom autocomplete
    &__select {
        .v-chip {
            color: #fff !important;
        }
    }

    // custom-chip
    &__comment-chip {
        background-color: #0eccaa !important;
        color: #fff !important;
    }
}

// TextField
.v-text-field {
    .v-field {
        border-radius: 10px !important;
    }
}

// VInput
.v-input {
    // fix for input details
    .v-input__details {
        padding-top: 2px;
        padding-bottom: 4px;
    }
}

.v-field--variant-outlined.v-field--focused .v-field__outline {
    --v-field-border-width: 1px;
}

// Vuetify switch customization
.v-switch {
    .v-selection-control {
        &--dirty {
            .v-switch__track {
                opacity: 1 !important;
            }
        }
    }
}
//----------------
</style>
